import type { ColumnDef } from '@tanstack/react-table';
import type { InvoicingActivityGroupBy, InvoicingActivityMetrics, InvoicingActivityRow } from '../../../services/accountingReports.service';
import { formatCurrency } from '../../../utils/money';

type Metric = keyof InvoicingActivityMetrics;
const METRICS: [Metric, string][] = [['pronumbers', 'Pronumbers'], ['invoices', 'Invoices'], ['amount', 'Invoice Amt']];
const value = (row: InvoicingActivityRow, period: string | null, metric: Metric) =>
  (period === null ? row.total[metric] : row.periods[period]?.[metric]) ?? 0;
const format = (number: number, metric: Metric) => metric === 'amount' ? formatCurrency(number) : String(number);

/** V1's clerk-by-period matrix, with flat leaf labels that remain clear in Excel. */
export function invoicingActivityColumns(periods: string[], groupBy: InvoicingActivityGroupBy): ColumnDef<InvoicingActivityRow>[] {
  const result: ColumnDef<InvoicingActivityRow>[] = [
    { id: 'clerk', accessorKey: 'clerk', header: 'Invoice Clerk', size: 220, footer: 'Grand Total' },
  ];
  for (const period of [...periods, null]) {
    const label = period === null ? 'Grand Total' : `${groupBy === 'WEEK' ? 'Week of ' : ''}${period}`;
    for (const [metric, title] of METRICS) {
      result.push({
        id: `${period ?? 'total'}_${metric}`,
        accessorFn: row => value(row, period, metric),
        header: `${label} · ${title}`, size: metric === 'amount' ? 205 : 195,
        cell: ({ getValue }) => format(Number(getValue()), metric),
        meta: { aggType: 'sum' },
        footer: ({ table }) => format(table.getFilteredRowModel().rows.reduce((sum, row) => sum + value(row.original, period, metric), 0), metric),
      });
    }
  }
  return result;
}

/** Export the same filtered totals without introducing sortable fake data rows. */
export function activityExportFooter(periods: string[], rows: InvoicingActivityRow[]): Record<string, string> {
  const footer: Record<string, string> = { clerk: 'Grand Total' };
  for (const period of [...periods, null]) {
    for (const [metric] of METRICS) {
      footer[`${period ?? 'total'}_${metric}`] = format(rows.reduce((sum, row) => sum + value(row, period, metric), 0), metric);
    }
  }
  return footer;
}
